import React, { useState, useEffect } from 'react'
import { Users, ShoppingBag, Clock, TrendingUp } from 'lucide-react'

export default function SocialProof() {
  const [activeShops, setActiveShops] = useState(0)
  const [ordersProcessed, setOrdersProcessed] = useState(0)
  const [hoursSaved, setHoursSaved] = useState(0)

  // Animated counter effect
  useEffect(() => {
    const animateCounter = (setter, target, duration = 2000) => {
      let start = 0
      const increment = target / (duration / 16)
      const timer = setInterval(() => {
        start += increment
        if (start >= target) {
          setter(target)
          clearInterval(timer)
        } else {
          setter(Math.floor(start))
        }
      }, 16)
      return timer
    }

    const timer1 = animateCounter(setActiveShops, 50)
    const timer2 = animateCounter(setOrdersProcessed, 10000)
    const timer3 = animateCounter(setHoursSaved, 2400)

    return () => {
      clearInterval(timer1)
      clearInterval(timer2)
      clearInterval(timer3)
    }
  }, [])

  const stats = [
    {
      icon: Users,
      number: `${activeShops}+`,
      label: 'Active Shops',
      subtext: 'Trust LaundryMate',
      color: 'from-cyan-500 to-blue-500'
    },
    {
      icon: ShoppingBag,
      number: `${(ordersProcessed / 1000).toFixed(1)}K+`,
      label: 'Orders Processed',
      subtext: 'This Month',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: Clock,
      number: `${(hoursSaved / 1000).toFixed(1)}K+`,
      label: 'Hours Saved',
      subtext: 'Weekly',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: TrendingUp,
      number: '35%',
      label: 'Avg Revenue Growth',
      subtext: 'First 6 Months',
      color: 'from-orange-500 to-red-500'
    }
  ]

  return (
    <section className="py-16 bg-gradient-to-br from-gray-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800 transition-colors duration-300">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Join the Growing
            <span className="gradient-text"> LaundryMate Community</span>
          </h2>
          <p className="text-lg text-gray-600 dark:text-gray-400">
            Trusted by laundry shop owners nationwide
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="group bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 relative overflow-hidden"
            >
              {/* Gradient background on hover */}
              <div className={`absolute inset-0 bg-gradient-to-br ${stat.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />

              {/* Icon */}
              <div className={`w-14 h-14 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                <stat.icon className="w-7 h-7 text-white" />
              </div>

              {/* Number */}
              <div className={`text-4xl font-bold bg-gradient-to-br ${stat.color} bg-clip-text text-transparent mb-2`}>
                {stat.number}
              </div>

              {/* Label */}
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                {stat.label}
              </h3>

              {/* Subtext */}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {stat.subtext}
              </p>
            </div>
          ))}
        </div>

        {/* Trust Badges */}
        <div className="mt-12 flex flex-wrap justify-center items-center gap-8 text-gray-600 dark:text-gray-400">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <span className="font-medium">98% Satisfaction Rate</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="font-medium">24/7 Support Available</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <span className="font-medium">Secure & Reliable</span>
          </div>
        </div>
      </div>
    </section>
  )
}
